/*
 * Quick add from the collection card - Way Sauce #933.
 *
 * Single-variant products post straight to the cart. Two-variant products open an option
 * sheet and the Add button stays disabled until one is chosen.
 *
 * The cart it opens is whatever the theme has - the #932 cart drawer once that ships, the
 * existing cart notification until then. Same lookup product-form.js uses, so nothing here
 * needs changing when the drawer goes live.
 */

if (!customElements.get('quick-add-product')) {
  customElements.define(
    'quick-add-product',
    class QuickAddProduct extends HTMLElement {
      constructor() {
        super();

        this.openButton = this.querySelector('[data-quick-add-open]');
        this.sheet = this.querySelector('[data-quick-add-sheet]');
        this.submit = this.querySelector('[data-quick-add-submit]');
        this.errorEl = this.querySelector('[data-quick-add-error]');
        const label = this.submit && this.submit.querySelector('[data-quick-add-submit-label]');
        this.addToCartText = label ? label.textContent.trim() : '';

        this.onKeyUp = this.handleKeyUp.bind(this);
        this.boundClick = this.onClick.bind(this);
        this.boundChange = this.onChange.bind(this);
        this.addEventListener('click', this.boundClick);
        this.addEventListener('change', this.boundChange);
      }

      connectedCallback() {
        this.portalSheet();
      }

      disconnectedCallback() {
        // do not leave an orphan sheet on <body> if the grid is ever re-rendered
        if (this.sheet && this.sheet.parentElement === document.body) this.sheet.remove();
      }

      /*
       * The sheet is position: fixed, and a transformed ancestor becomes the containing block
       * for fixed positioning - so the card's hover lift re-anchored the sheet to the card,
       * which moved it out from under the cursor, which dropped the hover, which snapped it
       * back. That loop is the flicker Eli hit.
       *
       * Moving the sheet to <body> once makes it immune to any ancestor transform, this one or
       * any the theme adds later. Its listeners move with it, since it no longer bubbles to
       * this element.
       */
      portalSheet() {
        if (!this.sheet || this.sheetPortaled) return;

        document.body.appendChild(this.sheet);
        this.sheet.addEventListener('click', this.boundClick);
        this.sheet.addEventListener('change', this.boundChange);
        this.sheetPortaled = true;
      }

      get cart() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      onClick(event) {
        if (event.target.closest('[data-quick-add-open]')) {
          event.preventDefault();
          // a single-variant product has nothing to ask, so it just adds
          if (this.dataset.variantId) {
            this.addToCart(this.dataset.variantId);
          } else {
            this.openSheet();
          }
          return;
        }

        if (event.target.closest('[data-quick-add-close]')) {
          event.preventDefault();
          this.closeSheet();
          return;
        }

        if (event.target.closest('[data-quick-add-submit]')) {
          event.preventDefault();
          // the radios live in the sheet, which is portaled to <body> - NOT inside this element
          const chosen = this.sheet && this.sheet.querySelector('input[type="radio"]:checked');
          if (chosen) this.addToCart(chosen.value);
        }
      }

      onChange(event) {
        if (event.target.type !== 'radio') return;
        // Add is disabled until a variant is picked - the ticket asks for exactly this
        this.submit.disabled = false;
        this.clearError();

        // The selected state is driven by a class rather than :has(input:checked) - that
        // selector matches in querySelector here but did not repaint on the checked change,
        // leaving the chosen row looking identical to the others.
        this.sheet.querySelectorAll('.quick-add__option').forEach((opt) => {
          opt.classList.toggle('quick-add__option--selected', opt.contains(event.target));
        });

        // put the chosen price on the button, so the choice is confirmed where the thumb is
        // about to land rather than only up in the list
        const label = this.submit.querySelector('[data-quick-add-submit-label]');
        if (label && event.target.dataset.price) {
          label.textContent = `${this.addToCartText} · ${event.target.dataset.price}`;
        }
      }

      /* ---------- the option sheet ---------- */

      openSheet() {
        if (!this.sheet) return;

        // the sheet is reused, so start every open from a clean slate rather than showing the
        // previous visit's choice already made
        this.resetSelection();

        clearTimeout(this.hideTimer);
        this.sheet.hidden = false;
        // let the element paint hidden-false before the transition class lands
        requestAnimationFrame(() => this.sheet.classList.add('quick-add__sheet--open'));

        this.lockScroll();
        document.addEventListener('keyup', this.onKeyUp);

        const panel = this.sheet.querySelector('.quick-add__panel');
        if (panel) trapFocus(panel, panel);
      }

      closeSheet() {
        if (!this.sheet) return;

        this.sheet.classList.remove('quick-add__sheet--open');
        this.unlockScroll();
        document.removeEventListener('keyup', this.onKeyUp);

        // focus goes back to the button that opened it, not to the top of the page
        removeTrapFocus(this.openButton);

        // Only hide if the sheet is still closed when this fires. Reopening inside the 400ms
        // window used to let the delayed hide land on top of the new open, which left the
        // sheet marked hidden with a zero-size panel.
        const hide = () => {
          if (!this.sheet.classList.contains('quick-add__sheet--open')) this.sheet.hidden = true;
        };
        const panel = this.sheet.querySelector('.quick-add__panel');
        if (panel && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          panel.addEventListener('transitionend', hide, { once: true });
          // never leave the sheet stuck open if the transition does not fire
          clearTimeout(this.hideTimer);
          this.hideTimer = setTimeout(hide, 400);
        } else {
          hide();
        }
      }

      handleKeyUp(event) {
        if (event.code === 'Escape') this.closeSheet();
      }

      /*
       * Locking body overflow removes the scrollbar, which widens the viewport by its width and
       * slides centred page content sideways - measured 7-8px on desktop, which is exactly half
       * of a 15px scrollbar. Replacing the scrollbar with equivalent padding keeps the layout
       * still while the sheet is open.
       *
       * Measured BEFORE the class goes on, because after that the scrollbar is already gone.
       */
      lockScroll() {
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbar > 0) {
          this.previousBodyPadding = document.body.style.paddingRight;
          document.body.style.paddingRight = `${scrollbar}px`;
        }
        document.body.classList.add('overflow-hidden');
      }

      unlockScroll() {
        document.body.classList.remove('overflow-hidden');
        if (this.previousBodyPadding !== undefined) {
          document.body.style.paddingRight = this.previousBodyPadding;
          this.previousBodyPadding = undefined;
        }
      }

      resetSelection() {
        if (!this.sheet) return;

        this.sheet.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
        });
        this.sheet.querySelectorAll('.quick-add__option').forEach((opt) => {
          opt.classList.remove('quick-add__option--selected');
        });

        if (this.submit) {
          this.submit.disabled = true;
          const label = this.submit.querySelector('[data-quick-add-submit-label]');
          if (label) label.textContent = this.addToCartText;
        }

        this.clearError();
      }

      /* ---------- adding ---------- */

      addToCart(variantId) {
        if (this.openButton.classList.contains('quick-add__button--busy')) return;

        this.setBusy(true);
        this.clearError();

        const cart = this.cart;
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const body = {
          id: variantId,
          quantity: 1,
        };

        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (cart.setActiveElement) cart.setActiveElement(this.openButton);
        }

        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((parsed) => {
            if (parsed.status) {
              this.showError(parsed.description || parsed.message);
              return;
            }

            this.showTick();
            this.closeSheet();
            if (cart) cart.renderContents(parsed);
          })
          .catch(() => {
            this.showError(window.cartStrings.error);
          })
          .finally(() => {
            this.setBusy(false);
          });
      }

      /* ---------- feedback ---------- */

      setBusy(busy) {
        this.openButton.classList.toggle('quick-add__button--busy', busy);
        this.openButton.setAttribute('aria-busy', busy ? 'true' : 'false');
        if (this.submit) this.submit.classList.toggle('quick-add__submit--busy', busy);
      }

      showTick() {
        this.openButton.classList.add('quick-add__button--added');
        clearTimeout(this.tickTimer);
        this.tickTimer = setTimeout(() => {
          this.openButton.classList.remove('quick-add__button--added');
        }, 1400);
      }

      showError(message) {
        const text = message || window.cartStrings.error;
        if (this.errorEl) {
          this.errorEl.textContent = text;
          this.errorEl.hidden = false;
        } else {
          // single-variant cards have no sheet to put a message in
          this.openButton.setAttribute('title', text);
          this.openButton.classList.add('quick-add__button--error');
          setTimeout(() => this.openButton.classList.remove('quick-add__button--error'), 2000);
        }
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }
  );
}
